<script setup lang="ts">
import { ref, onMounted } from 'vue';
const { getSelectableEnvironments, setDesiredEnvironment, getCurrentEnvironmentKey } = useApi(undefined, useCookie('desired_environment')?.value);
const { clearAuth } = useUser();

const props = withDefaults(
  defineProps<{
    autoSave: Boolean
  }>(),
  {
    autoSave: false
  }
)

const availableEnvironments = getSelectableEnvironments();

const currentEnvironmentKey = ref('');
const currentEnvironment = ref(null);

onMounted(() => {
  const key = getCurrentEnvironmentKey();
  currentEnvironmentKey.value = key;
  currentEnvironment.value = availableEnvironments[key] || null;
});

const handleSelectChange = (environmentKey) => {
  if (props.autoSave) saveData(environmentKey);
};

const saveData = (environmentKey: string) => {
  const environment = availableEnvironments[environmentKey];
  setDesiredEnvironment(environmentKey);
  currentEnvironment.value = environment;
};

const saveAndReload = () => {
  saveData(currentEnvironmentKey.value);
  clearAuth();
  window.location.href = '/';
};

const niceUrl = (url: string) => {
  return url.replace(/(^\w+:|^)\/\//, '');
};

const groupedEnvs = computed(() => {
  // Only build groups that actually have members, so we never render an empty
  // optgroup (e.g. "Production" once Demo Production is excluded).
  const groups: Record<string, Record<string, any>> = {};

  for (const [key, env] of Object.entries(availableEnvironments)) {
    (groups[env.envType] ??= {})[key] = env;
  }

  return groups;
})
</script>

<template>
  <div class="wrapper">
    <select v-model="currentEnvironmentKey" @change="handleSelectChange($event.target.value)"
      :class="{ 'full-width': props.autoSave }">
      <optgroup :label="group" v-for="(envs, group) in groupedEnvs">
        <option v-for="(e, key) in envs" :key="key" :value="key">
          {{ e.niceName }} ({{ niceUrl(e.apiBaseUrl) }})
        </option>
      </optgroup>
    </select>

    <button v-if="!autoSave" class="button button--smaller" @click="saveAndReload()">
      Save and reload
    </button>

    <a v-if="currentEnvironment" class="link" target="_blank" :href="currentEnvironment.apiBaseUrl">{{
      currentEnvironment.niceName }}
      FT CRM &raquo;</a>
  </div>
</template>

<style scoped>
select {
  -webkit-appearance: none;
  box-sizing: border-box;
  height: 30px;
  padding: 0 10px;
  border: none;
  border-radius: 4px;
  font-weight: 700;
  font-size: 14px;
  outline: none;
  cursor: pointer;
  padding-right: 30px;
  background-image: url('data:image/svg+xml;charset=utf8,<svg xmlns="http://www.w3.org/2000/svg" width="40" height="20" viewBox="0 0 40 20"><polygon points="0,0 40,0 20,20" fill="black"/></svg>');
  background-repeat: no-repeat;
  background-size: 30px 6px;
  background-position: center right;
}

select.full-width {
  width: 100%;
  background-color: #3D4071;
  color: #9D9FB5;
  font-weight: 300;
}

.wrapper {
  width: 100%;
  display: flex;
  flex-wrap: wrap;
  flex-direction: row;
  align-items: flex-start;
  gap: 10px;
  margin-top: 15px;
}
</style>